import React, { useEffect, useState } from 'react';
import { Button, Grid, Typography } from '@mui/material';
import { CancelButton } from '../../../components/buttons/CancelButton.component';
import { ConfirmButton } from '../../../components/buttons/ConfirmButton.component';
import { Modal } from '../../../components/Modal/Modal.component';
import CheckIcon from '@mui/icons-material/Check';
import { useLayoutContext } from '../../../Context/Layout.context';
import { useTranslation } from 'react-i18next';
import useDocumentTitle from '../../../hooks/DocumentTitle/documentTitle';
import {
  importExaminee,
  importExamineeApi,
  saveImportExaminee,
  uploadFile,
} from '../../../services/images/images.service';
import { AxiosResponse } from 'axios';
import FileInput from '../../../components/FileInput.component';
import loadingFileIcon from '../../../assets/icons/loadingFile.svg';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { useStyles as importTeamsUseStyles } from '../../ExaminerTreemap/Actions/ImportTeams.styles';
import { LeavePrompt } from '../../../components/Prompt/LeavePrompt/LeavePrompt.component';
import { examineeImportJson, examineeImportJsonSIOEO, examineeImportJsonSIOEPKZ } from '../../../const/exampleFile';
import { SystemId } from '../../../services/images/images.types';

interface ImportExamineeListProps {
  isOpen: boolean;
  handleClose: () => void;
  modalTitle: string;
  statistic?: string;
  sheetId: string;
  importFromFile: boolean;
  system?: string;
}

export const ImportExamineeListApi: React.FC<ImportExamineeListProps> = ({
  isOpen,
  handleClose,
  modalTitle,
  statistic,
  sheetId,
  importFromFile,
  system,
}) => {
  useDocumentTitle('Import zdających');
  const { showLoader, openAlert } = useLayoutContext();
  const { t } = useTranslation();
  const [leavePromptOpen, setLeavePromptOpen] = useState<boolean>(false);
  const [examineeFile, setExamineeFile] = useState<File>();
  const [isFileLoading, setIsFileLoading] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);

  const importTeamsClasses = importTeamsUseStyles();

  const handleConfirm = () => {
    showLoader(true);
    importExamineeApi(sheetId, system !== undefined ? system : '')
      .then((res: AxiosResponse) => {
        openAlert('success', `Przyjęto zlecenie importu zdających z ${system}`);
      })
      .catch(() => {
        openAlert('error', 'Zaimportowanie danych nie powiodło się, spróbuj ponownie później.');
      })
      .finally(() => {
        handleClose();
        showLoader(false);
      });
  };

  const systemId = () => {
    if (system === 'SIOEPKZ') {
      return 2;
    }
    if (system === 'SIOEO') {
      return 1;
    } else {
      return SystemId.Custom;
    }
  };

  const exampleFile = () => {
    if (system === 'SIOEPKZ') {
      return examineeImportJsonSIOEPKZ;
    }
    if (system === 'SIOEO') {
      return examineeImportJsonSIOEO;
    } else {
      return examineeImportJson;
    }
  };

  //Import from file

  const handleFileLoaded = (files: FileList) => {
    if (files.length === 1 && files[0].type === 'application/json') {
      setIsFileLoading(true);
      setExamineeFile(files[0]);
      setValidationError(undefined);
    } else {
      setValidationError('Przekazany plik jest nieprawidłowy - dopuszczalne są tylko pliki json');
    }
  };

  useEffect(() => {
    if (examineeFile !== undefined) {
      examineeFile
        .text()
        .then((stringifiedJson: string) => {
          try {
            if (Object.keys(stringifiedJson).length === 0) {
              setValidationError('Przekazany plik nie zawiera żadnych danych');
            } else {
              JSON.parse(stringifiedJson);
              setValidationError(undefined);
            }
          } catch (error) {
            setValidationError(
              error instanceof SyntaxError
                ? 'Przekazany plik nie jest zgodny z formatowaniem json'
                : 'Wystąpił błąd podczas ładowania.',
            );
          }
        })
        .catch((error) => setValidationError('Wystąpił błąd podczas ładowania.'))
        .finally(() => setIsFileLoading(false));
    }
  }, [examineeFile]);

  const handleOnConfirmFile = () => {
    if (examineeFile) {
      showLoader(true);
      uploadFile(examineeFile, sheetId, systemId())
        .then((result) => {
          openAlert('success', `Przyjęto zlecenie importu zdających z pliku`);
        })
        .catch(() => {
          openAlert('error', 'Zaimportowanie danych nie powiodło się, spróbuj ponownie później.');
        })
        .finally(() => {
          handleClose();
          showLoader(false);
        });
    }
  };

  const closeModal = () => {
    if (examineeFile !== undefined) {
      setLeavePromptOpen(true);
    } else handleClose();
  };

  return (
    <>
      <Modal
        data-testid={'importExamineeListTest'}
        open={isOpen}
        autoHeight={true}
        title="Zaimportuj zdających dla"
        xCloseButton={handleClose}
        size={'sm'}
      >
        <Grid item xs={12}>
          <Typography sx={{ marginLeft: 2, marginBottom: 5, fontWeight: 700, size: 16 }}>{modalTitle}</Typography>
        </Grid>
        <Grid container direction="column" alignItems="center" justifyContent="center">
          <Grid item xs={12}>
            <Typography
              sx={{
                marginTop: importFromFile ? 1 : 5,
                marginBottom: importFromFile ? 1 : 5,
                fontWeight: 700,
                size: 18,
              }}
            >
              {`Dane w systemie ${t('global:shortNames.system')}:`}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                marginBottom: importFromFile ? 10 : 150,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ margin: 0.5, fontWeight: 700, fontSize: 40, color: '#3C419A' }}>{statistic}</Typography>
              <Typography sx={{ margin: 0.5, fontWeight: 400, fontSize: 14, width: 62 }}>liczba zdających</Typography>
            </div>
          </Grid>
          {importFromFile && (
            <Grid justifyContent="center" container style={{ marginBottom: '50px' }}>
              <Grid item xs={10}>
                <FileInput
                  onFilesLoaded={handleFileLoaded}
                  hint="Zaimportowane pliki powinny mieć format json, zobacz przykładowy plik"
                  acceptedFileExtensions={['json']}
                  exampleFile={exampleFile()}
                />
              </Grid>
              {(examineeFile !== undefined || validationError?.length !== undefined) && (
                <Grid item xs={10} style={{ marginTop: '20px' }}>
                  <Grid container alignItems="center">
                    <Grid item xs={1}>
                      {isFileLoading ? (
                        <img src={loadingFileIcon} alt='ikona pobierania pliku' />
                      ) : validationError !== undefined ? (
                        <AddCircleIcon htmlColor="#BC0000" style={{ transform: 'rotate(45deg)' }} />
                      ) : (
                        <CheckCircleIcon htmlColor="#347F42" />
                      )}
                    </Grid>
                    <Grid item xs={10} style={{ textAlign: 'left' }}>
                      <Typography>{examineeFile !== undefined ? examineeFile.name : null}</Typography>
                    </Grid>
                    <Grid item xs={1}>
                      <Button
                        onClick={() => {
                          setExamineeFile(undefined), setValidationError(undefined);
                        }}
                        style={{
                          minWidth: 'unset',
                          color: 'rgba(44, 45, 62, 0.8)',
                        }}
                      >
                        <CloseIcon />
                      </Button>
                    </Grid>
                  </Grid>
                  <Grid>
                    {validationError !== undefined && (
                      <div className={importTeamsClasses.validationError}>{validationError}</div>
                    )}
                  </Grid>
                </Grid>
              )}
            </Grid>
          )}
          <Grid container sx={{ padding: 0.5, justifyContent: 'end', alignItems: 'center', margin: '10px' }}>
            <Grid item xs={3} style={{ marginRight: '15px' }}>
              <CancelButton onClick={closeModal}>Anuluj</CancelButton>
            </Grid>
            <Grid item xs={4} style={{ marginRight: '15px' }}>
              <ConfirmButton
                style={{
                  backgroundColor: 'green',
                  border: '1px solid green',
                  padding: '5px',
                  width: '180px',
                }}
                startIcon={<CheckIcon />}
                onClick={importFromFile ? handleOnConfirmFile : handleConfirm}
                disabled={importFromFile && examineeFile === undefined || validationError !== undefined}
              >
                Potwierdź import
              </ConfirmButton>
            </Grid>
          </Grid>
        </Grid>
      </Modal>
      <LeavePrompt
        isOpen={leavePromptOpen}
        onConfirm={() => {
          setExamineeFile(undefined);
          setLeavePromptOpen(false);
          handleClose();
        }}
        onCancel={() => setLeavePromptOpen(false)}
      />
    </>
  );
};
